import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SidebarNav from './SidebarNav';

const MessageDetailView = () => {
    const { messageid } = useParams();
    const navigate = useNavigate();
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Theme colors
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";
    const accentColor = "text-blue-400";
    const cardBg = "bg-gray-900/60";
    const contentBg = "bg-gray-950/50";
    const borderColor = "border-gray-700/30";

    useEffect(() => {
        const fetchMessage = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/messages/${messageid}`);
                setMessage(response.data.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch message details. Please try again later.');
                console.error("Error fetching message:", err);
                setLoading(false);
            }
        };

        if (messageid) {
            fetchMessage();
        }
    }, [messageid]);

    const formatDateTime = (isoString) => {
        if (!isoString) return 'N/A';
        try {
            return new Date(isoString).toLocaleString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit', second: '2-digit'
            });
        } catch (e) {
            return 'Invalid Date';
        }
    };

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* Sidebar */}
            <SidebarNav currentPath="/developer/messages" />

            {/* Main Content Area */}
            <div className="ml-64">
                {loading && (
                    <div className="container mx-auto p-6 pt-24 h-screen flex items-start justify-center">
                        <div className="animate-pulse flex flex-col items-center">
                            <div className={`h-8 w-48 ${cardBg} rounded-md mb-6`}></div>
                            <div className={`h-64 w-full max-w-2xl ${cardBg} rounded-lg`}></div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="container mx-auto p-6 pt-24 flex justify-center">
                        <div className={`p-6 text-red-400 ${cardBg} rounded-lg border border-red-900/30 max-w-2xl w-full`}>
                            <div className="flex items-center space-x-3 mb-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h2 className="text-xl font-medium">Error</h2>
                            </div>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && !message && (
                    <div className="container mx-auto p-6 pt-24 flex justify-center">
                        <div className={`p-6 ${textSecondary} ${cardBg} rounded-lg border ${borderColor} max-w-2xl w-full text-center`}>
                            Message not found.
                        </div>
                    </div>
                )}

                {!loading && !error && message && (
                    <div className="container mx-auto p-4 md:p-6 pt-24 md:pt-28 max-w-4xl">
                        <div className={`${cardBg} shadow-2xl rounded-lg p-6 md:p-8 border ${borderColor} backdrop-blur-sm relative`}>
                            {/* Back button in top left with hover effect */}
                            <button
                                onClick={() => navigate('/developer/messages')}
                                className="absolute top-6 left-6 flex items-center group py-1.5 px-3 rounded-md bg-gray-800/80 border border-gray-700/50 hover:bg-blue-900/30 hover:border-blue-800/50 transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
                                aria-label="Back to messages"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-5 w-5 mr-1.5 ${accentColor} group-hover:text-blue-300 transition-colors duration-200`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className={`${textPrimary} font-medium text-sm group-hover:text-blue-300 transition-colors duration-200`}>
                                    Back
                                </span>
                            </button>

                            <div className="mb-10">
                                <h1 className={`text-3xl md:text-4xl font-serif font-medium ${textPrimary} text-center`}>
                                    Message Details
                                </h1>
                                <div className="mt-3 w-24 h-1 mx-auto bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-60"></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8 border-b border-gray-700/20 pb-8">
                                <div className="mb-1">
                                    <p className={`text-sm font-medium ${textSecondary}`}>
                                        Date Received
                                    </p>
                                    <p className={`${textPrimary} text-lg mt-1 font-medium`}>
                                        {formatDateTime(message.createdAt)}
                                    </p>
                                </div>

                                <div className="mb-1">
                                    <p className={`text-sm font-medium ${textSecondary}`}>
                                        From
                                    </p>
                                    <p className={`${textPrimary} text-lg mt-1 font-medium`}>
                                        {message.name}
                                    </p>
                                </div>

                                <div className="mb-1">
                                    <p className={`text-sm font-medium ${textSecondary}`}>
                                        Email
                                    </p>
                                    <p className={`${textPrimary} text-lg mt-1 font-medium`}>
                                        {message.email}
                                    </p>
                                </div>

                                <div className="mb-1">
                                    <p className={`text-sm font-medium ${textSecondary}`}>
                                        Service Inquired
                                    </p>
                                    <p className={`${textPrimary} text-lg mt-1 font-medium`}>
                                        {message.service || 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className={`text-lg font-medium ${accentColor} mb-3`}>Message</p>
                                <div className={`whitespace-pre-wrap p-5 rounded-md ${contentBg} border ${borderColor} ${textPrimary} min-h-[180px] leading-relaxed shadow-inner`}>
                                    {message.message}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageDetailView;